import { useEffect, useEffectEvent } from 'react';

export const DISMISS_REASON = {
    ESCAPE: 'escape',
    OUTSIDE: 'outside',
};

export const DISMISS_KEY = 'Escape';

const OUTSIDE_EVENTS = ['mousedown', 'touchstart'];

const toArray = (value) => (Array.isArray(value) ? value : [value]);

/** Return true when `target` sits inside any of the given refs' nodes. */
export const isInsideRefs = (refs, target) =>
    toArray(refs).some((ref) => ref?.current && target instanceof Node && ref.current.contains(target));

/**
 * Call `onDismiss(event, reason)` on Escape and on a mousedown/touchstart outside `ref`, while
 * `enabled`. `ignore` lists further refs that count as inside — a portalled panel that is not a
 * DOM descendant of `ref`. `reason` is a `DISMISS_REASON`, so a caller can restore focus on
 * Escape only.
 */
const useDismiss = (ref, onDismiss, { enabled = true, ignore = [] } = {}) => {
    const dismiss = useEffectEvent((event, reason) => onDismiss?.(event, reason));
    const isInside = useEffectEvent((target) => isInsideRefs([ref, ...toArray(ignore)], target));

    useEffect(() => {
        if (!enabled) return undefined;

        const onKeyDown = (event) => {
            if (event.key === DISMISS_KEY) dismiss(event, DISMISS_REASON.ESCAPE);
        };
        const onPointerDown = (event) => {
            if (!isInside(event.target)) dismiss(event, DISMISS_REASON.OUTSIDE);
        };

        document.addEventListener('keydown', onKeyDown);
        OUTSIDE_EVENTS.forEach((type) => document.addEventListener(type, onPointerDown));
        return () => {
            document.removeEventListener('keydown', onKeyDown);
            OUTSIDE_EVENTS.forEach((type) => document.removeEventListener(type, onPointerDown));
        };
    }, [enabled]);
};

export default useDismiss;
